import { openDB, DBSchema, IDBPDatabase } from 'idb';
import {
  DB_NAME,
  DB_VERSION,
  STORE_NAMES,
  LocalPhoto,
  LocalFrame,
  LocalOutlet,
  SyncQueueItem,
} from './schema';

/**
 * IndexedDB Schema Definition (products and transactions no longer stored)
 */
interface PhotoBoothDB extends DBSchema {
  photos: {
    key: string;
    value: LocalPhoto;
    indexes: { transactionId: string; uploaded: IDBValidKey };
  };
  frames: {
    key: string;
    value: LocalFrame;
    indexes: { categoryId: string; paperType: string };
  };
  outlets: {
    key: string;
    value: LocalOutlet;
    indexes: { status: string; syncedAt: number };
  };
  syncQueue: {
    key: number;
    value: SyncQueueItem;
    indexes: { entityType: string; createdAt: number };
  };
}

let dbInstance: IDBPDatabase<PhotoBoothDB> | null = null;

/**
 * Initialize and open the IndexedDB database
 */
export async function initDB(): Promise<IDBPDatabase<PhotoBoothDB>> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<PhotoBoothDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion) {
      console.log(`Upgrading DB from version ${oldVersion} to ${newVersion}`);

      // Remove products and transactions stores if upgrading from older version
      if (oldVersion < 3) {
        const rawDb = db as unknown as IDBDatabase;
        if (rawDb.objectStoreNames.contains('products')) {
          rawDb.deleteObjectStore('products');
        }
        if (rawDb.objectStoreNames.contains('transactions')) {
          rawDb.deleteObjectStore('transactions');
        }
      }

      // Photos store
      if (!db.objectStoreNames.contains(STORE_NAMES.PHOTOS)) {
        const photoStore = db.createObjectStore(STORE_NAMES.PHOTOS, {
          keyPath: 'photoId',
        });
        photoStore.createIndex('transactionId', 'transactionId', { unique: false });
        photoStore.createIndex('uploaded', 'uploaded', { unique: false });
      }

      // Frames store
      if (!db.objectStoreNames.contains(STORE_NAMES.FRAMES)) {
        const frameStore = db.createObjectStore(STORE_NAMES.FRAMES, {
          keyPath: 'frameId',
        });
        frameStore.createIndex('categoryId', 'categoryId', { unique: false });
        frameStore.createIndex('paperType', 'paperType', { unique: false });
      }

      // Sync Queue store
      if (!db.objectStoreNames.contains(STORE_NAMES.SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORE_NAMES.SYNC_QUEUE, {
          keyPath: 'queueId',
          autoIncrement: true,
        });
        syncStore.createIndex('entityType', 'entityType', { unique: false });
        syncStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Outlets store
      if (!db.objectStoreNames.contains(STORE_NAMES.OUTLETS)) {
        const outletStore = db.createObjectStore(STORE_NAMES.OUTLETS, {
          keyPath: 'outletId',
        });
        outletStore.createIndex('status', 'status', { unique: false });
        outletStore.createIndex('syncedAt', 'syncedAt', { unique: false });
      }
    },
    blocked() {
      console.warn('DB upgrade blocked - close other tabs');
    },
    blocking() {
      console.warn('DB blocking - new version available');
      dbInstance?.close();
      dbInstance = null;
    },
    terminated() {
      console.error('DB connection terminated unexpectedly');
      dbInstance = null;
    },
  });

  return dbInstance;
}

/**
 * Get the database instance (initializes if needed)
 */
export async function getDB(): Promise<IDBPDatabase<PhotoBoothDB>> {
  if (!dbInstance) {
    return initDB();
  }
  return dbInstance;
}

/**
 * Close the database connection
 */
export function closeDB(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

/**
 * Delete the entire database (for testing/reset)
 */
export async function deleteDB(): Promise<void> {
  closeDB();
  
  // In test environment with fake-indexeddb, use a timeout to prevent hanging
  const deletePromise = new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    
    let resolved = false;
    const resolveOnce = () => {
      if (!resolved) {
        resolved = true;
        resolve();
      }
    };
    
    const rejectOnce = (error: any) => {
      if (!resolved) {
        resolved = true;
        reject(error);
      }
    };
    
    request.onsuccess = resolveOnce;
    request.onerror = () => rejectOnce(request.error);
    request.onblocked = () => {
      console.warn('DB deletion blocked');
      // In test environment, resolve anyway after a short delay
      setTimeout(resolveOnce, 100);
    };
  });
  
  // Add a timeout to prevent hanging in tests
  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(() => resolve(), 5000); // 5 second timeout
  });
  
  await Promise.race([deletePromise, timeoutPromise]);
}
